/**
 * QP-184 — multi-party clients (phase 1).
 *
 * `ClientGroupMember` = composition of a Group-type client (couples / joint
 * investors): either a link to an existing Person client or an inline
 * quick-add. `ClientParty` = representation & authority on ANY client type
 * (Company representatives, Person POA/guardian, Group representative) —
 * the substrate for MiFID II Art. 54(6) attribution, PEP screening and
 * signer linkage in later phases.
 */

/** Max members on a Group client (Argenta GAP-029/030). */
export const MAX_GROUP_MEMBERS = 20;

/**
 * QL-654 — max representatives per client: active related parties (Person,
 * Company, Group) or, for a Group with no party, members flagged as
 * representative. Every one is an answering subject, a billed identity check and
 * a screening query, so the service layer refuses a sixth on every surface.
 */
export const MAX_CLIENT_REPRESENTATIVES = 5;

export const CLIENT_PARTY_ROLES = ["representative", "poa", "guardian"] as const;
export type ClientPartyRole = (typeof CLIENT_PARTY_ROLES)[number];

/**
 * QL-505 — the role of whoever acts for a client: a party's own role, or
 * `"group_member"` for a Group's flagged representative member (no
 * per-member role concept).
 */
export type RepresentativeRole = ClientPartyRole | "group_member";

export const CLIENT_PARTY_AUTHORITY_BASES = [
  "signatory",
  "board_resolution",
  "poa",
  "fremtidsfullmakt",
  "vergemal",
] as const;
export type ClientPartyAuthorityBasis = (typeof CLIENT_PARTY_AUTHORITY_BASES)[number];

export type ClientGroupMember = {
  id: string;
  groupClientId: string;
  /** Link to an existing Person client; null = inline member. */
  memberClientId: string | null;
  /** Inline snapshot — when linked, the linked client's data displays and these are the fallback. */
  name: string;
  ssn: string | null;
  /** Screening identifier for an inline member; a linked member's DOB comes
   *  from their own Person client row. */
  dateOfBirth: string | null;
  email: string | null;
  phone: string | null;
  isRepresentative: boolean;
  /**
   * What entitles this member to represent the group. Runtime-validated against
   * CLIENT_PARTY_AUTHORITY_BASES. Null unless isRepresentative.
   */
  authorityBasis: string | null;
  order: number;
  /**
   * The linked client's live fields, populated when memberClientId is set AND
   * the caller may open it (QL-380); omitted otherwise, so a hidden client is
   * described only by this member row's own stored fields.
   */
  linkedClient?: { id: string; name: string; email: string | null; mobilePhone: string | null };
  /**
   * QL-380 — whether the caller may open the linked client. False when the link
   * is hidden from them (or there is no link); the member itself stays listed.
   */
  linkedClientAccessible: boolean;
  createdById: string;
  createdBy?: { id: string; name: string };
  updatedById: string;
  updatedBy?: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
};

export type CreateClientGroupMemberRequest = {
  memberClientId?: string | null;
  name?: string;
  ssn?: string | null;
  dateOfBirth?: string | null;
  email?: string | null;
  phone?: string | null;
  isRepresentative?: boolean;
  authorityBasis?: ClientPartyAuthorityBasis | null;
};

export type UpdateClientGroupMemberRequest = CreateClientGroupMemberRequest;

export type CreateClientPartyRequest = {
  role: ClientPartyRole;
  name: string;
  ssn?: string | null;
  dateOfBirth?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  authorityBasis?: ClientPartyAuthorityBasis | null;
  authorityScope?: string | null;
  validFrom?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
};

export type UpdateClientPartyRequest = Partial<CreateClientPartyRequest>;

/**
 * QL-661 — the fields EVERY representative carries, whichever endpoint lists
 * them: `/clients/{id}/representatives` (registered parties, expired ones
 * included) and `/clients/{id}/acting-representatives` (who acts today:
 * non-expired parties, else a Group's flagged members). Same shape on the
 * internal routes. The endpoints differ in meaning, not in shape.
 */
export type RepresentativeFields = {
  /**
   * `client_party.id` for a party, `client_group_member.id` for a flagged group
   * member (QL-654) — except (QL-653) one of SEVERAL representatives of a
   * flagged member's own client, who is addressed by their own `client_party.id`
   * (only on `/acting-representatives`, see `ActingRepresentative.actingForMemberId`).
   */
  id: string;
  /** Same value as `id` — the address used on the product-assessment and quiz endpoints (QL-654). */
  subjectId: string;
  name: string;
  ssn: string | null;
  /** Screening identifier when the representative has no national id. ISO date. */
  dateOfBirth: string | null;
  email: string | null;
  phone: string | null;
  /** QL-62 — the party's own eID country; null = fall back to the client's. */
  country: string | null;
  /** `party` — a registered related party; `group_member` — a Group member flagged as representative. */
  source: "party" | "group_member";
  /**
   * QL-505 — `ClientPartyRole` ("representative"/"poa"/"guardian") when
   * `source` is `"party"`; `"group_member"` when the acting representative is
   * a flagged Group member (no per-member role concept). Drives
   * `InviteRecipientResponse.role`.
   */
  role: RepresentativeRole;
  /**
   * Runtime-validated against CLIENT_PARTY_AUTHORITY_BASES. A plain flagged
   * `group_member`'s own basis — EXCEPT when that member is itself a linked
   * Person client with its own active representative: then this is THAT
   * nested representative's basis (spec QL-661 D4), same as `role` already
   * follows the nested party.
   */
  authorityBasis: string | null;
  /**
   * Free-text scope / mandate limits (e.g. "investments up to 1 MNOK"). Null
   * for a plain `group_member`; the nested representative's own scope when
   * the member is represented in turn (see `authorityBasis`).
   */
  authorityScope: string | null;
  /** ISO date; null for a plain `group_member`, else the nested representative's own (see `authorityBasis`). */
  validFrom: string | null;
  /** ISO date; null for a plain `group_member`, else the nested representative's own (see `authorityBasis`). */
  expiresAt: string | null;
  /** The `order` of the row `id` refers to: among the client's parties, or among the group's members. */
  order: number;
};

/**
 * One acting representative of a client (QP-184 resolver): non-expired
 * related parties win, a Group falls back to its members flagged as
 * representative, otherwise the client acts for itself. QL-654 — there can be
 * several; with two or more, each of them answers for themselves.
 */
export type ActingRepresentative = RepresentativeFields & {
  /**
   * QL-653 — set when this entry is one of SEVERAL representatives of a flagged
   * group member's own client (each answers for that member slot): the
   * `client_group_member.id` of that member. `id`/`subjectId` are then the
   * party's own `client_party.id`; `order` stays the member's (entries of one
   * member share it, in their own party order). Null otherwise (with ONE nested
   * representative `id` is the member's id, as before).
   */
  actingForMemberId: string | null;
};

/** `representatives: []` = client acts for itself (missing client is a 404). In `order`. */
export type ActingRepresentativesResponse = {
  representatives: ActingRepresentative[];
};

/**
 * A registered related party as the public and internal list endpoints return
 * it (QL-661): the shared representative fields plus the stored row's own
 * metadata. Always `source: "party"`.
 */
export type Representative = RepresentativeFields & {
  source: "party";
  role: ClientPartyRole;
  clientId: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Internal shape: `Representative` with the audit users the advisor UI shows. */
export type ClientParty = Representative & {
  /**
   * QL-692 — the server's verdict: not expired as of today in the tenant zone.
   * Clients read this instead of comparing `expiresAt` to their own "today".
   */
  isActive: boolean;
  createdById: string;
  createdBy?: { id: string; name: string };
  updatedById: string;
  updatedBy?: { id: string; name: string };
};

/**
 * QL-62 — jurisdictions whose eID Criipto can drive (see
 * criipto-signature-service: acrValues / scope are picked per country).
 * Anything else falls back to no:bankid, which is why a missing or
 * unsupported country blocks eID signing.
 */
export const EID_SIGNATURE_COUNTRIES = ["NO", "SE", "DK", "FI"] as const;
export type EidSignatureCountry = (typeof EID_SIGNATURE_COUNTRIES)[number];

/**
 * QL-62 — the jurisdiction a signature order should drive eID from.
 *
 * The acting party's own country wins, but only when we have a provider for
 * it: an unsupported one would fall through to Criipto's no:bankid default
 * without the login hint and `openid ssn` scope the client's own country
 * would have supplied — weaker evidence than ignoring the party country.
 */
export function resolveEidCountry(
  partyCountry: string | null | undefined,
  clientCountry: string | null | undefined,
): string | null {
  const party = partyCountry?.trim().toUpperCase();
  if (party && (EID_SIGNATURE_COUNTRIES as readonly string[]).includes(party)) return party;
  // Normalized, but NOT filtered to the supported set: an unsupported client
  // country behaves identically to null in Criipto (both land on the
  // no:bankid default) while still being the truthful value for the order's
  // metadata. getSignatureReadiness is what judges whether eID can work.
  return clientCountry?.trim().toUpperCase() || null;
}

/** The contact/identity fields a signature method needs from whoever signs. */
export type SignerContact = {
  ssn?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
};

/**
 * QL-62 — which signature methods this signer can actually complete.
 *
 * Simple signature is a link delivered by email or SMS, so either channel is
 * enough. eID needs an SSN to validate the evidence against and a supported
 * eID jurisdiction. Shared by the service layer and the client-party UI so
 * the warning an advisor sees is the same rule the backend applies.
 */
export function getSignatureReadiness(signer: SignerContact): {
  simple: boolean;
  eid: boolean;
} {
  const has = (value?: string | null) => !!value?.trim();
  const country = signer.country?.trim().toUpperCase();
  return {
    simple: has(signer.email) || has(signer.phone),
    eid:
      has(signer.ssn) &&
      !!country &&
      (EID_SIGNATURE_COUNTRIES as readonly string[]).includes(country),
  };
}
